// Google Analytics 4 (gtag.js) integration.
//
// Activates only when VITE_GA_MEASUREMENT_ID is set at build time, so local dev
// and test builds are silent no-ops (no script load, no network calls). Set the
// var in dashboard/.env for local testing or via the deploy build-arg for prod.
//
// This is an SPA, so GA4's automatic page_view tracking is disabled and page
// views are sent manually on route changes (see trackPageView / RouteTracker).

const MEASUREMENT_ID = import.meta.env.VITE_GA_MEASUREMENT_ID;

declare global {
  interface Window {
    dataLayer: unknown[];
    gtag: (...args: unknown[]) => void;
  }
}

let initialized = false;

export function isAnalyticsEnabled(): boolean {
  return Boolean(MEASUREMENT_ID);
}

/** Inject gtag.js and configure the GA4 property. Safe to call more than once. */
export function initAnalytics(): void {
  if (initialized || !MEASUREMENT_ID) return;
  initialized = true;

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${MEASUREMENT_ID}`;
  document.head.appendChild(script);

  window.dataLayer = window.dataLayer || [];
  // IMPORTANT: gtag.js only treats a dataLayer item as a command when it is the
  // native `arguments` object — internally it checks
  // Object.prototype.toString(item) === "[object Arguments]" (or a `callee`
  // prop). A rest-param real array ([...]) fails that check, so the command —
  // including the initial `config` call — is silently dropped and NOTHING is
  // ever sent to GA. We must push `arguments` exactly like Google's canonical
  // snippet, which requires a classic function (not an arrow).
  window.gtag = function () {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer.push(arguments);
  };
  window.gtag('js', new Date());
  // Page views are sent manually on route change (SPA), so disable auto.
  window.gtag('config', MEASUREMENT_ID, { send_page_view: false });
}

/** Record a SPA page view. */
export function trackPageView(path: string, title?: string): void {
  if (!MEASUREMENT_ID || !window.gtag) return;
  window.gtag('event', 'page_view', {
    page_path: path,
    page_location: window.location.origin + path,
    page_title: title ?? document.title,
  });
}

/** Record a custom event. Param values should be primitives GA4 can index. */
export function trackEvent(name: string, params: Record<string, unknown> = {}): void {
  if (!MEASUREMENT_ID || !window.gtag) return;
  window.gtag('event', name, params);
}
